import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { VerifiedBadge } from '@/components/VerifiedBadge';
import { DocumentModal } from '@/components/DocumentModal';
import { Document } from '@/components/DocumentCard';

// Product interface matching architecture specification
interface PaymentTerms {
  pricePerUnitUsd: number;
  totalUnits: number;
  totalVolumeMt: number;
  totalDueUsd: number;
}

interface LogisticsInfo {
  packaging: string;
  shipmentCompany: string;
  contractDuration: string;
  paymentDuration: string;
}

interface ComplianceInfo {
  minimumGrade: string;
  inspectionsNote: string;
}

interface Product {
  id: string;
  name: string;
  category: string;
  commodityType: string;
  origin: string;
  regions: string[];
  verified: boolean;
  image?: string;
  paymentTerms: PaymentTerms;
  logistics: LogisticsInfo;
  compliance: ComplianceInfo;
}

// Mock product data - extended version with all required fields
const mockProducts: Product[] = [
  {
    id: '1',
    name: 'Brent Crude FOB',
    category: 'Crude Oil',
    commodityType: 'Oil',
    origin: 'UAE',
    regions: ['Middle East', 'Europe'],
    verified: true,
    image: '/images/products/oil_barrelss.jpg',
    paymentTerms: {
      pricePerUnitUsd: 85.50,
      totalUnits: 100,
      totalVolumeMt: 100,
      totalDueUsd: 8550.00,
    },
    logistics: {
      packaging: 'Bulk',
      shipmentCompany: 'ABC Logistics',
      contractDuration: '30 days',
      paymentDuration: '15 days',
    },
    compliance: {
      minimumGrade: 'API 38°',
      inspectionsNote: 'Contact admin',
    },
  },
  {
    id: '2',
    name: 'Diesel Grade A',
    category: 'Refined Products',
    commodityType: 'Oil',
    origin: 'USA',
    regions: ['North America'],
    verified: true,
    image: '/images/products/oil_barrelss.jpg',
    paymentTerms: {
      pricePerUnitUsd: 92.00,
      totalUnits: 50,
      totalVolumeMt: 50,
      totalDueUsd: 4600.00,
    },
    logistics: {
      packaging: 'Bulk',
      shipmentCompany: 'XYZ Shipping',
      contractDuration: '45 days',
      paymentDuration: '20 days',
    },
    compliance: {
      minimumGrade: 'Grade A',
      inspectionsNote: 'Contact admin',
    },
  },
  {
    id: '3',
    name: 'WTI Crude',
    category: 'Crude Oil',
    commodityType: 'Oil',
    origin: 'USA',
    regions: ['North America'],
    verified: false,
    image: '/images/products/wheat.jpg',
    paymentTerms: {
      pricePerUnitUsd: 84.00,
      totalUnits: 200,
      totalVolumeMt: 200,
      totalDueUsd: 16800.00,
    },
    logistics: {
      packaging: 'Bulk',
      shipmentCompany: 'Global Transport',
      contractDuration: '60 days',
      paymentDuration: '30 days',
    },
    compliance: {
      minimumGrade: 'API 40°',
      inspectionsNote: 'Contact admin',
    },
  },
];

/**
 * ProductDetailPage component - Full product detail page
 * 
 * Features:
 * - Product header with name, verified badge, and image
 * - Back button to return to marketplace
 * - General Information section
 * - Payment Terms section
 * - Shipping & Logistics section
 * - Inspections & Compliance section
 * - Action buttons: View Documents, Negotiate, Buy Now
 * - Responsive layout: 2-column on desktop, single column on mobile/tablet
 * - Loading and error states
 */
export const ProductDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState<boolean>(false);

  // Fetch product data (mock implementation)
  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      setError(null);

      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 500));

      if (!id) {
        setError('Product ID is required');
        setLoading(false);
        return;
      }

      const foundProduct = mockProducts.find(p => p.id === id);
      
      if (!foundProduct) {
        setError('Product not found');
        setLoading(false);
        return;
      }

      setProduct(foundProduct);
      setLoading(false);
    };

    fetchProduct();
  }, [id]);

  // Format currency
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  // Format quantity with MT unit
  const formatQuantity = (quantity: number): string => {
    return `${quantity} MT`;
  };

  // Handle back button click
  const handleBack = () => {
    navigate('/marketplace');
  };

  // Handle Buy Now button click
  const handleBuyNow = () => {
    if (product) {
      navigate(`/buy/${product.id}/step-1`);
    }
  };

  // Handle View Documents button click
  const handleViewDocuments = () => {
    setIsDocumentModalOpen(true);
  };

  // Mock document data - matches Architecture section 4.7
  const getMockDocuments = (productId: string): Document[] => {
    const baseDate = new Date();
    return [
      {
        id: `doc-1-${productId}`,
        ownerUserId: 'user-1',
        productId: productId,
        type: 'allocation_declaration',
        fileName: 'allocation_declaration.pdf',
        url: '/images/products/oil_barrelss.jpg', // Placeholder image
        status: 'approved',
        uploadedAt: new Date(baseDate.getTime() - 7 * 24 * 60 * 60 * 1000),
        approvedAt: new Date(baseDate.getTime() - 6 * 24 * 60 * 60 * 1000),
      },
      {
        id: `doc-2-${productId}`,
        ownerUserId: 'user-1',
        productId: productId,
        type: 'inspection_certificate',
        fileName: 'inspection_certificate.pdf',
        url: '/images/products/oil_barrelss.jpg', // Placeholder image
        status: 'approved',
        uploadedAt: new Date(baseDate.getTime() - 5 * 24 * 60 * 60 * 1000),
        approvedAt: new Date(baseDate.getTime() - 4 * 24 * 60 * 60 * 1000),
      },
      {
        id: `doc-3-${productId}`,
        ownerUserId: 'user-1',
        productId: productId,
        type: 'warehouse_receipt',
        fileName: 'warehouse_receipt.pdf',
        url: '/images/products/oil_barrelss.jpg', // Placeholder image
        status: 'pending',
        uploadedAt: new Date(baseDate.getTime() - 3 * 24 * 60 * 60 * 1000),
      },
      {
        id: `doc-4-${productId}`,
        ownerUserId: 'user-1',
        productId: productId,
        type: 'certificate_of_origin',
        fileName: 'certificate_of_origin.pdf',
        url: '/images/products/oil_barrelss.jpg', // Placeholder image
        status: 'approved',
        uploadedAt: new Date(baseDate.getTime() - 2 * 24 * 60 * 60 * 1000),
        approvedAt: new Date(baseDate.getTime() - 1 * 24 * 60 * 60 * 1000),
      },
      {
        id: `doc-5-${productId}`,
        ownerUserId: 'user-1',
        productId: productId,
        type: 'other',
        fileName: 'other_document.pdf',
        url: '/images/products/oil_barrelss.jpg', // Placeholder image
        status: 'pending',
        uploadedAt: new Date(baseDate.getTime() - 1 * 24 * 60 * 60 * 1000),
      },
    ];
  };

  // Handle Negotiate button click (placeholder for Epic 8)
  const handleNegotiate = () => {
    // Placeholder - will be implemented in Epic 8
    console.log('Negotiate - to be implemented in Epic 8');
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background w-full">
        <div className="flex items-center justify-center py-32">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
            <p className="text-base text-text-secondary">Loading product details...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !product) {
    return (
      <div className="min-h-screen bg-background w-full">
        <div className="flex items-center justify-center py-32">
          <div className="text-center">
            <p className="text-h2 font-bold text-text-primary mb-2">Product Not Found</p>
            <p className="text-base text-text-secondary mb-6">{error || 'The product you are looking for does not exist.'}</p>
            <button
              onClick={handleBack}
              className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors font-medium"
            >
              Return to Marketplace
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Back Button */}
      <button
        onClick={handleBack}
        className="mb-6 flex items-center gap-2 text-text-secondary hover:text-text-primary transition-colors"
        aria-label="Back to marketplace"
      >
        <ArrowLeft className="w-5 h-5" />
        <span className="text-base font-medium">Back to Marketplace</span>
      </button>

      {/* Main Content - Split 50/50 on desktop, stacked on mobile */}
      <div className="grid grid-cols-1 desktop:grid-cols-2 gap-8 mb-8 w-full">
        {/* Left Column: "Buy a product" text, product name + badge, product image */}
        <div className="flex flex-col min-w-0">
          {/* "Buy a product" text */}
          <p className="text-base text-text-secondary mb-4">Buy a product</p>
          
          {/* Product Name and Verified Badge in same row */}
          <div className="flex items-center gap-3 mb-4">
            <h1 className="text-h1 font-bold text-text-primary">{product.name}</h1>
            {product.verified && <VerifiedBadge />}
          </div>

          {/* Product Image - Constrained size */}
          <div className="w-full">
            {product.image ? (
              <img
                src={product.image}
                alt={product.name}
                className="w-full h-auto rounded-lg object-cover max-h-[400px]"
              />
            ) : (
              <div className="w-full bg-background-secondary rounded-lg flex items-center justify-center max-h-[400px]">
                <span className="text-text-tertiary">No image available</span>
              </div>
            )}
          </div>
        </div>

        {/* Right Column: All product sections in a single card */}
        <div className="flex flex-col gap-6 min-w-0">
          {/* Card wrapper for all sections */}
          <div className="bg-background border border-border rounded-lg shadow-md p-6 flex flex-col gap-6">
            {/* General Information Section */}
            <div>
              <h2 className="text-h2 font-bold text-text-primary mb-4">General Information</h2>
              <div className="space-y-3">
                <div>
                  <span className="text-sm font-medium text-text-secondary">Place of origin:</span>
                  <p className="text-base text-text-primary mt-1">{product.origin}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-text-secondary">Region(s):</span>
                  <p className="text-base text-text-primary mt-1">{product.regions.join(', ')}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-text-secondary">Commodity type:</span>
                  <p className="text-base text-text-primary mt-1">{product.commodityType}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-text-secondary">Category:</span>
                  <p className="text-base text-text-primary mt-1">{product.category}</p>
                </div>
              </div>
            </div>

            <hr className="border-border" />

            {/* Payment Terms Section */}
            <div>
              <h2 className="text-h2 font-bold text-text-primary mb-4">Payment Terms</h2>
              <div className="space-y-3">
                <div>
                  <span className="text-sm font-medium text-text-secondary">Units:</span>
                  <p className="text-base text-text-primary mt-1">{formatQuantity(product.paymentTerms.totalUnits)}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-text-secondary">Price per unit:</span>
                  <p className="text-base text-text-primary mt-1">{formatCurrency(product.paymentTerms.pricePerUnitUsd)}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-text-secondary">Total volume:</span>
                  <p className="text-base text-text-primary mt-1">{formatQuantity(product.paymentTerms.totalVolumeMt)}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-text-secondary">Total due:</span>
                  <p className="text-base text-text-primary mt-1 font-semibold">{formatCurrency(product.paymentTerms.totalDueUsd)}</p>
                </div>
              </div>
            </div>

            <hr className="border-border" />

            {/* Shipping & Logistics Section */}
            <div>
              <h2 className="text-h2 font-bold text-text-primary mb-4">Shipping & Logistics</h2>
              <div className="space-y-3">
                <div>
                  <span className="text-sm font-medium text-text-secondary">Packaging:</span>
                  <p className="text-base text-text-primary mt-1">{product.logistics.packaging}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-text-secondary">Shipment company:</span>
                  <p className="text-base text-text-primary mt-1">{product.logistics.shipmentCompany}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-text-secondary">Contract duration:</span>
                  <p className="text-base text-text-primary mt-1">{product.logistics.contractDuration}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-text-secondary">Payment duration:</span>
                  <p className="text-base text-text-primary mt-1">{product.logistics.paymentDuration}</p>
                </div>
              </div>
            </div>

            <hr className="border-border" />

            {/* Inspections & Compliance Section */}
            <div>
              <h2 className="text-h2 font-bold text-text-primary mb-4">Inspections & Compliance</h2>
              <div className="space-y-3">
                <div>
                  <span className="text-sm font-medium text-text-secondary">Minimum grade:</span>
                  <p className="text-base text-text-primary mt-1">{product.compliance.minimumGrade}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-text-secondary">Inspections:</span>
                  <p className="text-base text-text-primary mt-1">{product.compliance.inspectionsNote}</p>
                </div>
              </div>
            </div>

            <hr className="border-border" />

            {/* Action Buttons - View Documents and Negotiate on first row, Buy Now on second row */}
            <div className="flex flex-col gap-4 items-center pt-2">
              <div className="flex gap-4 justify-center">
                <button
                  onClick={handleViewDocuments}
                  className="px-6 py-3 border border-border rounded-lg hover:bg-background-secondary transition-colors font-medium text-primary whitespace-nowrap"
                  aria-label="View product documents"
                >
                  View Documents
                </button>
                <button
                  onClick={handleNegotiate}
                  className="px-6 py-3 border border-border rounded-lg hover:bg-background-secondary transition-colors font-medium text-primary whitespace-nowrap"
                  aria-label="Negotiate product terms"
                >
                  Negotiate
                </button>
              </div>
              <button
                onClick={handleBuyNow}
                className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors font-medium whitespace-nowrap"
                aria-label="Buy this product now"
              >
                Buy Now
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Document Modal */}
      {product && (
        <DocumentModal
          isOpen={isDocumentModalOpen}
          onClose={() => setIsDocumentModalOpen(false)}
          productId={product.id}
          documents={getMockDocuments(product.id)}
        />
      )}
    </div>
  );
};
